import { useEffect, useState, useCallback } from 'react';
import { clsx } from 'clsx';
import {
  InformationCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useNotificationStore, type Notification } from '@/stores/notificationStore';

const typeStyles: Record<Notification['type'], string> = {
  info: 'bg-blue-500',
  success: 'bg-green-500',
  warning: 'bg-yellow-500',
  error: 'bg-red-500',
};

const typeIcons: Record<Notification['type'], typeof InformationCircleIcon> = {
  info: InformationCircleIcon,
  success: CheckCircleIcon,
  warning: ExclamationTriangleIcon,
  error: XCircleIcon,
};

interface ToastItemProps {
  notification: Notification;
  onDismiss: () => void;
}

function ToastItem({ notification, onDismiss }: ToastItemProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const handleDismiss = useCallback(() => {
    setIsLeaving(true);
    setTimeout(onDismiss, 300);
  }, [onDismiss]);

  useEffect(() => {
    // Animate in
    requestAnimationFrame(() => {
      setIsVisible(true);
    });

    // Auto dismiss after 5 seconds
    const timer = setTimeout(() => {
      handleDismiss();
    }, 5000);

    return () => clearTimeout(timer);
  }, [handleDismiss]);

  return (
    <div
      className={clsx(
        'flex items-center gap-3 p-4 rounded-lg shadow-lg text-white transform transition-all duration-300 max-w-sm',
        typeStyles[notification.type],
        isVisible && !isLeaving
          ? 'translate-x-0 opacity-100'
          : 'ltr:translate-x-full rtl:-translate-x-full opacity-0'
      )}
      dir="rtl"
    >
      <div className="flex-shrink-0">
        {(() => {
          const Icon = typeIcons[notification.type];
          return <Icon className="w-5 h-5" />;
        })()}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-sm">{notification.title}</h4>
        <p className="text-sm opacity-90 truncate">{notification.message}</p>
      </div>
      <button
        onClick={handleDismiss}
        className="flex-shrink-0 opacity-70 hover:opacity-100"
        aria-label="סגור הודעה"
      >
        <XMarkIcon className="w-4 h-4" />
      </button>
    </div>
  );
}

export function ToastContainer() {
  const { notifications, removeNotification } = useNotificationStore();
  // Toasts for `persistent` notifications (e.g. low stock) must not call removeNotification -
  // that deletes them from the bell/notification center entirely, not just the popup. This set
  // tracks "popup dismissed" locally so the toast can disappear from the corner while the
  // underlying notification stays active/unread until the person actually clicks it in the bell.
  const [locallyDismissed, setLocallyDismissed] = useState<Set<string>>(new Set());

  const visibleToasts = notifications
    .filter((n) => !n.read && !locallyDismissed.has(n.id))
    .slice(0, 3);

  const handleToastDismiss = (notification: Notification) => {
    if (notification.persistent) {
      setLocallyDismissed((prev) => new Set(prev).add(notification.id));
    } else {
      removeNotification(notification.id);
    }
  };

  if (visibleToasts.length === 0) return null;

  return (
    <div
      className="fixed top-4 right-4 z-50 flex flex-col gap-2"
      role="region"
      aria-live="polite"
      aria-atomic="true"
      aria-label="התראות מערכת"
    >
      {visibleToasts.map((notification) => (
        <ToastItem
          key={notification.id}
          notification={notification}
          onDismiss={() => handleToastDismiss(notification)}
        />
      ))}
    </div>
  );
}
